/**
 * What is still missing before a staged action may be confirmed.
 *
 * The agent stages from conversation, so a card can legitimately arrive
 * half-filled: a place inferred from "the Safwa yard" has a name and a
 * rough pin but nobody to call at the gate. Confirming that writes a
 * record the dispatcher can't use, so the card blocks its own Confirm
 * button and names what's missing instead of failing server-side.
 *
 * Pure and payload-driven so the card, the collapsed stack row, and
 * Confirm-all all reach the same verdict.
 */
export type ConfirmationBlocker = 'name' | 'location' | 'contact' | 'price';

const SAVED_LOCATION_ACTIONS = ['create_saved_location', 'update_saved_location'];

export const getConfirmationBlockers = (
  action: string,
  payload: Record<string, unknown>,
): ConfirmationBlocker[] => {
  if (action === 'edit_order') return needsPriceChoice(payload) ? ['price'] : [];
  if (!SAVED_LOCATION_ACTIONS.includes(action)) return [];

  const blockers: ConfirmationBlocker[] = [];
  if (!hasText(payload.name)) blockers.push('name');
  if (!hasPin(payload.location)) blockers.push('location');
  if (!hasContact(payload.contacts)) blockers.push('contact');
  return blockers;
};

/**
 * A cross-city move reprices the order, and the card asks which price to
 * use. Confirming with the custom option emptied would post a patch with
 * no price at all, so the button waits — a policy (the resolver's rate, or
 * the recommendation a never-expanded card still carries) or a real number
 * counts as an answer, a blank field does not.
 */
const needsPriceChoice = (payload: Record<string, unknown>): boolean => {
  const preview = payload.locationChangePreview as
    | { crossCity?: unknown; blockedReason?: unknown }
    | undefined;
  if (!preview || preview.crossCity !== true || preview.blockedReason) return false;
  if (typeof payload.locationChangePricing === 'string') return false;
  return typeof payload.setPrice !== 'number' || payload.setPrice <= 0;
};

const hasText = (value: unknown): boolean => typeof value === 'string' && value.trim().length > 0;

/** `{ lat: 0, lng: 0 }` is the "no pin" sentinel the staging tools use. */
const hasPin = (value: unknown): boolean => {
  if (typeof value !== 'object' || value === null) return false;
  const point = value as { lat?: unknown; lng?: unknown };
  if (typeof point.lat !== 'number' || typeof point.lng !== 'number') return false;
  return point.lat !== 0 || point.lng !== 0;
};

const hasContact = (value: unknown): boolean =>
  Array.isArray(value) && value.some(entry => (entry as { _id?: unknown })?._id != null);
